import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, Heart, Lightbulb, TrendingUp, ArrowRight, Shield, Award, Users, Clock, CheckCircle, Zap, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { teamMembers, stats } from '../mock';
import ContactPopup from '../components/ContactPopup';

const About = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Global Filing Protection®</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-6">
              We are a premier intellectual property law firm dedicated to protecting brands and innovations across the United States. Our mission is to make trademark protection accessible, efficient, and affordable for businesses at every stage of growth.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              With thousands of successful trademark registrations and a proven track record of excellence, we've become the trusted partner for entrepreneurs, startups, and established corporations seeking comprehensive IP protection.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-amber-500 mb-2">{stat.number}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Mission</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Empowering businesses to protect their most valuable assets with expert guidance and unwavering commitment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-gray-300 text-lg leading-relaxed">
                At Global Filing Protection®, we believe that every business deserves robust intellectual property protection, regardless of size or budget. Our mission is to democratize access to world-class trademark services while maintaining the highest standards of legal expertise and customer care.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                We understand that your brand is more than just a name or logo—it's your reputation, your identity, and your promise to customers. That's why we approach every trademark registration with meticulous attention to detail and a deep commitment to your success.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Our team combines extensive legal knowledge with cutting-edge technology to streamline the trademark process, making it faster, more transparent, and more affordable than traditional law firms.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { icon: Shield, title: 'Comprehensive Protection', desc: 'Full-spectrum IP services covering all aspects of brand protection' },
                { icon: Zap, title: 'Fast & Efficient', desc: 'Streamlined processes that get your trademark filed quickly' },
                { icon: Users, title: 'Client-Focused', desc: 'Personalized attention and dedicated support throughout your journey' },
                { icon: Award, title: 'Proven Excellence', desc: 'Industry-leading success rates and satisfied clients nationwide' }
              ].map((item, index) => (
                <Card key={index} className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 p-3 rounded-lg flex-shrink-0">
                        <item.icon className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">What Sets Us Apart</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Experience the difference of working with a firm that puts your success first.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                  <BookOpen className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white text-xl">Deep Expertise</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-400 space-y-4">
                <p>
                  Our team consists of seasoned trademark attorneys and IP specialists with decades of combined experience. We've handled everything from simple word marks to complex international trademark portfolios.
                </p>
                <p>
                  Every application is personally reviewed by a qualified attorney, ensuring accuracy and maximizing your chances of approval. We don't just file paperwork—we provide strategic guidance.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white text-xl">Transparent Process</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-400 space-y-4">
                <p>
                  No hidden fees, no surprises. Our pricing is clear and upfront, including all USPTO filing fees. You'll know exactly what you're paying for from day one.
                </p>
                <p>
                  We provide regular updates throughout the registration process and maintain open communication. You're never left wondering about the status of your trademark application.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                  <Target className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white text-xl">Results-Driven</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-400 space-y-4">
                <p>
                  With a 98% success rate, we have one of the highest approval ratings in the industry. Our comprehensive search process and strategic filing approach minimize rejections and office actions.
                </p>
                <p>
                  We stand behind our work with responsive support for any USPTO inquiries or office actions. Your success is our success, and we're committed to seeing your trademark through to registration.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Approach</h2>
            <p className="text-xl text-gray-400">
              A systematic, client-centered methodology that delivers results.
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                title: 'Comprehensive Consultation',
                desc: 'We begin by understanding your business, brand vision, and long-term goals. This allows us to provide tailored advice on the best trademark strategy for your specific situation.'
              },
              {
                title: 'Thorough Research & Analysis',
                desc: 'Before filing, we conduct exhaustive searches across federal databases, state records, common law uses, and online presence to identify potential conflicts and assess the strength of your mark.'
              },
              {
                title: 'Strategic Filing',
                desc: 'We carefully craft your application to maximize protection while minimizing the risk of rejection. Our attorneys select the appropriate classifications and draft precise descriptions of goods and services.'
              },
              {
                title: 'Proactive Monitoring',
                desc: 'Throughout the examination process, we closely monitor your application and respond promptly to any USPTO inquiries. We handle office actions efficiently and effectively.'
              },
              {
                title: 'Ongoing Support',
                desc: 'Even after registration, we provide guidance on trademark maintenance, renewal deadlines, and enforcement strategies to keep your brand protected for years to come.'
              }
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-4 bg-slate-900/40 border border-white/20 rounded-lg p-6 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-1">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              The principles that guide every decision we make and every client we serve.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="h-7 w-7 text-amber-500" />
                  </div>
                  <CardTitle className="text-white text-xl">Excellence in Everything</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  We maintain the highest standards in every aspect of our service—from initial consultation to final registration. Excellence isn't just a goal; it's our baseline. Every trademark application receives the same meticulous attention, whether it's from a solo entrepreneur or a Fortune 500 company.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="h-7 w-7 text-amber-500" />
                  </div>
                  <CardTitle className="text-white text-xl">Integrity & Transparency</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  Honesty guides every interaction. We provide clear, upfront pricing with no hidden fees, realistic timelines, and candid assessments of your trademark's viability. If we don't think trademark registration is right for you, we'll tell you. Your trust is more valuable than any single transaction.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="h-7 w-7 text-amber-500" />
                  </div>
                  <CardTitle className="text-white text-xl">Innovation & Efficiency</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  We embrace technology and modern processes to make trademark registration faster and more affordable without sacrificing quality. By automating routine tasks, we free our attorneys to focus on what matters most—strategic guidance and personalized client service.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-7 w-7 text-amber-500" />
                  </div>
                  <CardTitle className="text-white text-xl">Partnership & Growth</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  We view ourselves as long-term partners in your business journey. Your growth is our success. We're committed to helping you build and protect a valuable brand that can scale with your business, providing ongoing support as your IP portfolio expands.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Our expertise spans diverse industries, providing specialized trademark protection tailored to your sector's unique needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Technology & Software', desc: 'Protecting innovative tech brands, SaaS companies, and digital products in the rapidly evolving technology landscape.' },
              { name: 'E-Commerce & Retail', desc: 'Securing brand identity for online sellers, Amazon FBA businesses, and retail establishments across all platforms.' },
              { name: 'Food & Beverage', desc: 'Trademark protection for restaurants, food products, craft beverages, and culinary brands of all sizes.' },
              { name: 'Fashion & Apparel', desc: 'Safeguarding clothing lines, accessories, and fashion brands from design infringement and counterfeit products.' },
              { name: 'Professional Services', desc: 'Protecting consulting firms, agencies, and service providers with distinctive brand identities.' },
              { name: 'Health & Wellness', desc: 'Trademark registration for fitness brands, wellness products, healthcare services, and medical practices.' },
              { name: 'Entertainment & Media', desc: 'Securing intellectual property for content creators, production companies, and entertainment ventures.' },
              { name: 'Manufacturing', desc: 'Protecting product names, logos, and brand identities for manufacturers across all industries.' },
              { name: 'Real Estate', desc: 'Trademark services for real estate firms, property developers, and housing brands.' }
            ].map((industry, index) => (
              <Card key={index} className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-white text-lg">{industry.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm">{industry.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-6">
              Don't see your industry? We work with businesses across all sectors. Every brand deserves protection.
            </p>
            <Button 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-lg"
            >
              Discuss Your Industry Needs
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/20 rounded-2xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Let's Protect Your Brand Together
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Ready to work with a team that truly cares about your success? Get in touch today.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Contact Us Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default About;
